import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

// Logo URL - Fallen Dragon Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_fallen-events-hub/artifacts/9a9pm8c7_Fallen%20Dragon%20Logo.jpeg";

// Forest background
const FOREST_BG = "https://images.unsplash.com/photo-1573339607881-208e75e4b267?auto=format&fit=crop&w=1920&q=80";

const LegalNotice = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Helmet>
        <title>Legal Notice | Fallen Dragon Events</title>
        <meta name="description" content="Fallen Dragon Events Legal Notice - Company information, terms of use, intellectual property rights, and disclaimers." />
      </Helmet>

      {/* Background */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat opacity-20"
        style={{ backgroundImage: `url(${FOREST_BG})` }}
      />
      <div className="fixed inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/90" />

      {/* Header Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="Fallen Dragon Events" className="h-12 w-auto" />
          </Link>
          <div className="hidden md:flex items-center space-x-10">
            <Link to="/#about" className="text-white hover:text-gray-200 transition-colors text-sm tracking-[0.2em] uppercase">About</Link>
            <Link to="/#events" className="text-white hover:text-gray-200 transition-colors text-sm tracking-[0.2em] uppercase">Events</Link>
            <Link to="/#gallery" className="text-white hover:text-gray-200 transition-colors text-sm tracking-[0.2em] uppercase">Gallery</Link>
            <Link to="/#contact" className="text-white hover:text-gray-200 transition-colors text-sm tracking-[0.2em] uppercase">Contact</Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="relative z-10 pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-[#c9a55a] mb-8">Legal Notice</h1>

          {/* Content Sections */}
          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-[#c9a55a] mb-4">Company Information</h2>
              <div className="text-gray-300 space-y-2">
                <p><strong className="text-white">Fallen Dragon Events</strong></p>
                <p>Specialist Airsoft Events and Hard LARP</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#c9a55a] mb-4">Disclaimer</h2>
              <p className="text-gray-300 leading-relaxed">
                Fallen Dragon Events provides this website and its content on an "as-is" 
                and "as available" basis without any representations or warranties of any kind, express or implied. 
                We do not guarantee the accuracy, completeness, or suitability of the information and materials 
                provided on this website. Use of this website is at the user's own risk, and Fallen Dragon Events 
                shall not be held liable for any damages or loss arising from the information provided.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#c9a55a] mb-4">Limitation of Liability</h2>
              <p className="text-gray-300 leading-relaxed">
                Fallen Dragon Events offers airsoft-related activities that carry inherent risks. Participants must 
                read and sign a separate liability waiver before participating in any activity. Fallen Dragon Events 
                is not responsible for any accidents or injuries resulting from activities provided, provided that 
                all necessary safety protocols have been observed.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#c9a55a] mb-4">Privacy Policy</h2>
              <p className="text-gray-300 leading-relaxed">
                Fallen Dragon Events is committed to protecting your privacy. We collect and process personal data 
                in compliance with the General Data Protection Regulation (GDPR). Please see our{' '}
                <Link to="/privacy-policy" className="text-[#c9a55a] hover:underline">Privacy Policy</Link> for details on how we handle 
                your data, including collection, storage, and sharing.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#c9a55a] mb-4">Terms & Conditions</h2>
              <p className="text-gray-300 leading-relaxed">
                By using our services, you agree to our terms of service including booking policies, 
                cancellation procedures, and participant requirements. Please see our{' '}
                <Link to="/terms" className="text-[#c9a55a] hover:underline">Terms & Conditions</Link> for full details.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#c9a55a] mb-4">Intellectual Property</h2>
              <p className="text-gray-300 leading-relaxed">
                All materials on this website, including but not limited to text, images, logos, and videos, are the 
                intellectual property of Fallen Dragon Events, unless otherwise stated. Unauthorized use, reproduction, 
                or distribution of these materials without permission is strictly prohibited.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#c9a55a] mb-4">Third-Party Links</h2>
              <p className="text-gray-300 leading-relaxed">
                Our website may contain links to third-party websites for your convenience. We do not endorse or take 
                responsibility for the content, privacy policies, or practices of these external websites. Accessing 
                third-party sites from our website is done at your own risk.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#c9a55a] mb-4">Governing Law</h2>
              <p className="text-gray-300 leading-relaxed">
                This legal notice and any disputes related to the use of this website are governed by the laws of 
                the United Kingdom.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-[#c9a55a] mb-4">Changes to This Legal Notice</h2>
              <p className="text-gray-300 leading-relaxed">
                Fallen Dragon Events reserves the right to modify this legal notice at any time. Changes will be 
                posted on this page and are effective immediately. Your continued use of the website following any 
                updates signifies acceptance of the revised terms.
              </p>
            </section>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-gray-800 text-gray-500">
            <p>© Fallen Dragon Events</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalNotice;
